/* eslint-disable no-magic-numbers */

import Decimal from 'decimal.js';

import Regions from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/enums/regions';

import {
    hoursPlanned,
    subtaskDuration,
    workPaceRatio,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    hoursToMinutes,
    hoursToSeconds,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

const INFINITY = '9999';

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param region
 * @returns
 */
const exertionDuration = (assignment:any, region:number) => {
    let result = new Decimal(0);
    assignment.subtasks.forEach((subtask:any) => {
        result = result.plus(new Decimal(exertionDurationSubtaskPlanned(assignment, subtask, region)));
    });
    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @param region
 * @returns
 */
const exertionDurationSubtask = (assignment: any, subtask:any, region: number) => {
    let duration = new Decimal(0);
    let frequency = new Decimal(0);
    let durationSubtask = 0;
    if (region === Regions.Shoulder) {
        duration = new Decimal(subtask.durationShoulder);
        frequency = new Decimal(subtask.frequencyShoulder);
        durationSubtask = subtask.durationSubtaskShoulder;
    } else if (region === Regions.Back) {
        duration = new Decimal(subtask.durationBack);
        frequency = new Decimal(subtask.frequencyBack);
        durationSubtask = subtask.durationSubtaskBack;
    }

    const effectiveDurationMultiplier = new Decimal(subtask.effectiveDuration);
    const subtaskDurationHours = subtaskDuration(assignment, durationSubtask);
    const subtaskDurationMinutes = new Decimal(hoursToMinutes(subtaskDurationHours));

    return duration.times(frequency).times(subtaskDurationMinutes).times(effectiveDurationMultiplier).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @param region
 * @returns
 */
const exertionDurationSubtaskPlanned = (assignment:any, subtask:any, region:number) => {
    let duration = new Decimal(0);
    let durationSubtask = 0;
    if (region === Regions.Shoulder) {
        duration = new Decimal(subtask.durationShoulder);
        durationSubtask = subtask.durationSubtaskShoulder;
    } else if (region === Regions.Back) {
        duration = new Decimal(subtask.durationBack);
        durationSubtask = subtask.durationSubtaskBack;
    }

    const frequency = new Decimal(frequencyPlanned(assignment, subtask, region));
    const effectiveDurationMultiplier = new Decimal(subtask.effectiveDuration);
    const subtaskDurationHours = subtaskDuration(assignment, durationSubtask);
    const subtaskDurationMinutes = new Decimal(hoursToMinutes(subtaskDurationHours));

    return duration.times(frequency).times(subtaskDurationMinutes).times(effectiveDurationMultiplier).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @param region
 * @returns
 */
const frequencyPlanned = (assignment:any, subtask:any, region:number) => {
    let frequency = new Decimal(0);
    if (region === Regions.Shoulder) {
        frequency = new Decimal(subtask.frequencyShoulder);
    } else if (region === Regions.Back) {
        frequency = new Decimal(subtask.frequencyBack);
    }

    const actualRate = new Decimal(assignment.trial.actualRate);
    const wpRatio = new Decimal(workPaceRatio(assignment));

    return frequency.div(actualRate).times(wpRatio).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtask
 * @param region
 * @returns
 */
const madc = (subtask:any, region:any) => {
    let level = new Decimal(0);
    if (region === Regions.Shoulder) {
        level = new Decimal(subtask.levelShoulder);
    } else if (region === Regions.Back) {
        level = new Decimal(subtask.levelBack);
    }

    let result = new Decimal(0);
    if (level.greaterThan(result)) {
        const one = new Decimal(1);
        const seconds = new Decimal(hoursToSeconds(8)); // Assume 8 hour sift, not considering break time

        // (1-L/100)^4.167+1/(8*60*60))
        const base = one.minus(level.div(100));
        const power = base.pow(4.167);
        result = power.plus(one.div(seconds));
    }

    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param region
 * @returns
 */
export const raRatio = (assignment:any, region:number) => {
    const r = new Decimal(recoveryTimeRequired(assignment, region));
    const a = new Decimal(recoveryTimeAvailable(assignment, region));

    let result = '0';
    if (a.toNumber() <= 0) {
        result = INFINITY;
    } else if (r.toNumber() > 0) {
        result = r.div(a).toString();
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param jobPlan
 * @param region
 * @returns
 */
export const rcraRatio = (jobPlan:any, region:number) => {
    const r = new Decimal(recoveryTimeRequiredTotal(jobPlan, region));
    const a = new Decimal(recoveryTimeAvailableTotal(jobPlan, region));

    let result = '0';
    if (a.toNumber() <= 0) {
        result = INFINITY;
    } else if (r.toNumber() > 0) {
        result = r.div(a).toString();
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param region
 * @returns
 */
const recoveryTimeAvailable = (assignment:any, region:number) => {
    const hours = hoursPlanned(assignment);
    const seconds = new Decimal(hoursToSeconds(hours));
    const duration = new Decimal(exertionDuration(assignment, region));

    return seconds.minus(duration).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param jobPlan
 * @param region
 * @returns
 */
const recoveryTimeAvailableTotal = (jobPlan:any, region:number) => {
    let result = new Decimal(0);
    jobPlan.forEach((assignment:any) => {
        result = result.plus(new Decimal(recoveryTimeAvailable(assignment, region)));
    });
    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param region
 * @returns
 */
const recoveryTimeRequired = (assignment:any, region:number) => {
    let result = new Decimal(0);
    assignment.subtasks.forEach((subtask:any) => {
        result = result.plus(new Decimal(recoveryTimeRequiredSubtask(assignment, subtask, region)));
    });
    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @param region
 * @returns
 */
const recoveryTimeRequiredSubtask = (assignment:any, subtask:any, region:number) => {
    let result = new Decimal(0);
    const exertionDuration = new Decimal(exertionDurationSubtask(assignment, subtask, region));
    if (exertionDuration.greaterThan(result)) {
        const exertionDurationPlanned = new Decimal(exertionDurationSubtaskPlanned(assignment, subtask, region));
        const dutyCycle = new Decimal(madc(subtask, region));

        result = exertionDurationPlanned.div(dutyCycle).minus(exertionDurationPlanned);
    }
    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param jobPlan
 * @param region
 * @returns
 */
const recoveryTimeRequiredTotal = (jobPlan:any, region:number) => {
    let result = new Decimal(0);
    jobPlan.forEach((assignment:any) => {
        result = result.plus(recoveryTimeRequired(assignment, region));
    });
    return result.toString();
};